import { ReactComponent as IconChevron } from '@/assets/svgs/profile-chevron.svg'
import Button from '@/components/Button'
import { ToastContext } from '@/context/toast/index'
import usePackages from '@/hooks/use-packages/index'
import usePage from '@/hooks/use-page'
import useTemplates from '@/hooks/use-templates/index'
import { useNavigate, useQuery } from '@/lib/router-dom/index'
import { useFormik } from 'formik'
import { useContext, useEffect } from 'react'
import * as yup from 'yup'

const validationSchema = yup.object({
	name: yup
		.string('Enter your Package Name')
		.required('Name is required')
		.test('name-char-validation', 'Input field allows only Alphanumeric, Hyphen or Space as characters.', function (value) {
			let regex = /^[\w\-\s]+$/
			return regex.test(value)
		})
		.max(42, 'Package name should be maximum of 42 characters length')
		.nullable(),
	templateId: yup
		.string('Select your Template')
		.required('Template is required')
		.nullable(),
	referenceNumber: yup
		.string('Enter your Reference Number')
		.required('Reference Number is required')
		.test('reference-char-validation', 'Input field allows only Alphanumeric, Hyphen or Space as characters.', function (value) {
			let regex = /^[\w\-\s]+$/
			return regex.test(value)
		})
		.min(8, 'Reference Number should be minimum of 8 characters length')
		.max(32, 'Reference Number should be maximum of 32 characters length')
		.nullable(),
})

const initialValues = {
	id: 'test',
	name: '',
	templateId: '',
	referenceNumber: '',
};

const Page = () => {
	/*=================================================
	  General
	/================================================*/

	const pageTitle = 'Create Package';

	const { goTo } = useNavigate();
	const query = useQuery();
	const { addToastMessage } = useContext(ToastContext);
	const { createPackage } = usePackages();
	const { fetch, items: templates } = useTemplates();

	const {
		errors,
		touched,
		setFieldValue,
		handleSubmit,
		values,
		isValid,
		dirty,
		handleChange,
		handleBlur
	} = useFormik({
		validationSchema,
		initialValues,
		onSubmit: async (data) => {
			const response = await createPackage(data)
			goTo(`/package/details/${response.id}`)
			addToastMessage('Package successfully created', 'success')
		},
	})

	const handleCancel = () => {
		goTo('/package')
	}

	const { breadcrumbsItems } = usePage({
		title: pageTitle,
		breadcrumbs: ['Package Sharing'],
	})

	const errorMessage = (name) => {
		return errors[name] && touched[name] ? (
			<div className="error-message">{errors[name]}</div>
		) : <></>
	}

	useEffect(() => {
		fetch()

		// Hardcoded expectedDateOfCompletion, as backend Eclip Entity has expectedDateOfCompletion as required field.
		setFieldValue('expectedDateOfCompletion', new Date());
	}, [])

	useEffect(() => {
		const selectedTemplateId = query.get('templateId')
		setFieldValue('templateId', selectedTemplateId)
	}, [query])

	return (
		<div className="eclip-create__content">
			<div className="title-header">
				<div className="title-header__left">
					<div className="title-header__title">{pageTitle}</div>
				</div>

				<div className="title-header__right"></div>
			</div>

			<form className="eclip-create__form" onSubmit={handleSubmit}>
				<div className="eclip-create__form-row">
					<div className="eclip-create__input-label">{'Name of Package'}</div>
					<input
						className="eclip-create__input-field"
						name="name"
						placeholder={'Enter name'}
						value={values.name}
						onChange={handleChange}
						onBlur={handleBlur}
						data-testid="input_name"
					/>
					{errorMessage("name")}
				</div>
				<div className="eclip-create__form-row">
					<div className="eclip-create__input-label">{'Reference number'}</div>
					<input
						className="eclip-create__input-field"
						name="referenceNumber"
						placeholder={'Enter reference number'}
						value={values.referenceNumber}
						onChange={handleChange}
						onBlur={handleBlur}
						data-testid="input_referenceNumber"
					/>
					<div className="eclip-create__input-description">
						You will be using this to search for this package. We suggest you use a recognisable reference ID that is already used within your organisation.
					</div>
					{errorMessage("referenceNumber")}
				</div>
				<div className="eclip-create__form-row">
					<div className="eclip-create__input-label">{'Template'}</div>
					<div className="select">
						<div className="select__icon">
							<IconChevron />
						</div>
						<div className="select__options">
							<select
								name="templateId"
								value={values.templateId}
								onChange={handleChange}
								placeholder={'Select'}
								data-testid="select_templateId"
								onBlur={handleBlur}
							>
								<option value="" disabled>
									{'Select'}
								</option>
								{templates.map((template, index) => (
									<option key={template.id} value={template.id}>
										{template.name}
									</option>
								))}
							</select>
						</div>
					</div>
					{errorMessage("templateId")}
				</div>
				<div className="DS__layout__footer">
					<div className="layout-default__sticky-actions">
						<div className="layout-default__sticky-actions__left">
							<Button
								text={'Cancel'}
								sizing="Medium"
								full
								noborder
								maxWidth="154"
								submit={handleCancel}
								data-testid="button_cancel"
							/>
						</div>
						<div
							className="layout-default__sticky-actions__right"
							style={{ display: 'flex' }}
						>
							<div className="overview__button_wrap">
								<Button
									disabled={!(isValid && dirty)}
									text={'Create'}
									sizing="Medium"
									full
									maxWidth="154"
									type="submit"
									data-testid="button_next"
								/>
							</div>
						</div>
					</div>
				</div>
			</form>
		</div>
	)
}

export default Page
